import { useRef, type MouseEvent, type DragEvent } from 'react';
import type { Row, Table } from '@tanstack/react-table';

/** Modifier selection uses stable row IDs and the current displayed order. */
export function useDataGridSelection<T>(table: Table<T>, enabled: boolean) {
  const anchor = useRef<string>();

  const select = (row: Row<T>, range: boolean, toggle: boolean) => {
    const visible = table.getRowModel().rows;
    const next = Object.fromEntries(
      Object.entries(table.getState().rowSelection).filter(
        ([, selected]) => selected
      )
    );
    const anchorIndex = visible.findIndex(item => item.id === anchor.current);
    const rowIndex = visible.findIndex(item => item.id === row.id);
    if (range && anchorIndex >= 0 && rowIndex >= 0) {
      visible
        .slice(
          Math.min(anchorIndex, rowIndex),
          Math.max(anchorIndex, rowIndex) + 1
        )
        .forEach(item => {
          if (item.getCanSelect()) next[item.id] = true;
        });
    } else {
      if (toggle && next[row.id]) delete next[row.id];
      else if (row.getCanSelect()) next[row.id] = true;
      anchor.current = row.id;
    }
    table.setRowSelection(next);
    return visible.filter(item => next[item.id]).map(item => item.original);
  };

  const onClickCapture = (
    event: MouseEvent<HTMLTableRowElement>,
    row: Row<T>
  ) => {
    if (!enabled || !row.getCanSelect()) return;
    const target = event.target instanceof Element ? event.target : null;
    const selectionCell = target?.closest('[data-row-select]');
    if (
      !selectionCell &&
      target?.closest(
        'button, input, select, textarea, a, [role="button"], [role="combobox"], [contenteditable="true"]'
      )
    )
      return;

    if (
      event.shiftKey ||
      (!selectionCell && (event.ctrlKey || event.metaKey))
    ) {
      event.preventDefault();
      event.stopPropagation();
      select(row, event.shiftKey, !event.shiftKey);
    } else {
      // Normal row opening and checkbox toggling keep their existing handlers.
      anchor.current = row.id;
    }
  };

  const getDragSelection = (
    event: DragEvent<HTMLTableRowElement> | MouseEvent<HTMLTableRowElement>,
    row: Row<T>
  ) => {
    if (!enabled || !row.getCanSelect()) return undefined;
    const target = event.target instanceof Element ? event.target : null;
    if (
      !target?.closest('[data-row-select]') &&
      target?.closest(
        'button, input, select, textarea, a, [role="button"], [role="combobox"], [contenteditable="true"]'
      )
    )
      return undefined;
    if (event.shiftKey) return select(row, true, false);
    if ((event.ctrlKey || event.metaKey) && !row.getIsSelected()) {
      return select(row, false, false);
    }
    return undefined;
  };

  return { onClickCapture, getDragSelection };
}
